'use client';

import { 
  ControlType, 
  CalculatorId, 
  CalculatorType, 
  ControlIds,
  type CalculatorConfig,
  type ControlValues,
  type NumberControl,
  type CalculatorControl,
  DataTableInputMode
} from '../../../lib/calculators/calculator-config';
import { TableDataJSON } from '../../../src/types/table-json';
import { NormalCalculator, type NormalResult } from './normal-calculator';
import { calculateNormalDistribution } from '../../../lib/stats/distributions';

interface NormalControlValues extends ControlValues {
  mean: number;
  standardDeviation: number;
  value: number;
}

export const normalControls = {
  tabs: [
    {
      title: 'Parameters',
      controls: [
        {
          id: 'mean',
          type: ControlType.Number,
          label: 'Mean (μ)',
          required: true,
          defaultValue: 100,
          step: 1,
          min: -1000,
          max: 1000
        } satisfies NumberControl,
        {
          id: 'standardDeviation',
          type: ControlType.Number,
          label: 'Standard Deviation (σ)',
          required: true,
          min: 0.1,
          defaultValue: 15,
          step: 1,
          max: 100
        } satisfies NumberControl,
        {
          id: 'value',
          type: ControlType.Number,
          label: 'Value (x)',
          required: true,
          defaultValue: 125,
          step: 1,
          min: -1000,
          max: 1000
        } satisfies NumberControl
      ]
    }
  ]
} satisfies { tabs: { title: string; controls: readonly CalculatorControl[] }[] };

export const normalConfig: CalculatorConfig<CalculatorId.NORMAL_DISTRIBUTION> = {
  id: CalculatorId.NORMAL_DISTRIBUTION,
  type: CalculatorType.Graph,
  name: 'Normal Distribution Calculator',
  dataTableInputMode: DataTableInputMode.None,
  initialData: TableDataJSON.from({ columns: [] }), // No data needed
  initialControlValues: {
    mean: 100,
    standardDeviation: 15,
    value: 125
  },
  controls: normalControls,
  getInitialConfig: () => ({
    mean: 100,
    standardDeviation: 15,
    value: 125
  }),
  getVisualizationData: ({ controls }) => {
    const normalControls = controls as NormalControlValues;
    
    const mean = normalControls.mean;
    const stdDev = normalControls.standardDeviation;
    const value = normalControls.value;

    // Validate inputs
    if (
      mean === undefined ||
      stdDev === undefined ||
      value === undefined ||
      stdDev <= 0
    ) {
      return null;
    }

    const {
      pdf,
      cdf,
      xValues,
      probabilityLessThan,
      probabilityGreaterThan,
      zScore
    } = calculateNormalDistribution({
      mean,
      standardDeviation: stdDev,
      value
    });

    const result: NormalResult = {
      pdf,
      cdf,
      xValues,
      mean,
      standardDeviation: stdDev,
      zScore,
      probabilityLessThan,
      probabilityGreaterThan,
      value
    };
    
    return result;
  },
  render: ({ visualizationData }) => <NormalCalculator visualizationData={visualizationData} />
}; 